import { renderFormattedDate } from 'utils';
import { FormattedMessage } from 'react-intl';
import { User, ProjectReportComment } from 'library/types';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Popover, Box, Stack, Typography, IconButton, Tooltip, Divider } from '@mui/material';

type CommentsListPopoverProps<RowType extends object = object> = {
  open: boolean;
  anchorEl: HTMLElement | null;
  comments: ProjectReportComment[];
  currentUserId: number | string;
  onClose: () => void;
  onDeleteComment: (comment: ProjectReportComment, row: RowType) => void;
  row: RowType | null;
  users: User[];
};

const CommentsListPopover = <RowType extends object = object>({
  open,
  anchorEl,
  comments,
  currentUserId,
  onClose,
  onDeleteComment,
  row,
  users,
}: CommentsListPopoverProps<RowType>) => {
  const sortedComments = [...comments].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      slotProps={{
        paper: {
          sx: {
            width: 400,
            p: 2,
            maxHeight: 500,
            overflowY: 'auto',
          },
        },
      }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
        <FormattedMessage id="projectStepManaging.comments" defaultMessage="Yorumlar" />
      </Typography>
      <Divider sx={{ mb: 1.5 }} />
      <Stack spacing={2} divider={<Divider variant="middle" flexItem sx={{ opacity: 0.5 }} />}>
        {sortedComments.map((item) => {
          const isOwner = String(item.userId) === String(currentUserId);
          const user = users.find((u) => u.id === item.userId);
          const userName = user?.nameSurname || '...';

          return (
            <Box key={item.id} sx={{ position: 'relative', pr: isOwner ? 4 : 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                {renderFormattedDate(item.createdAt, true)} • {userName}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.4 }}>
                {item.commentText}
              </Typography>

              {isOwner && (
                <Box sx={{ position: 'absolute', top: 0, right: 0 }}>
                  <Tooltip
                    title={<FormattedMessage id="projectStepManaging.deleteComment" defaultMessage="Yorum Sil" />}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (row) onDeleteComment(item, row);
                      }}>
                      <DeleteOutlineIcon fontSize="small" color="error" sx={{ opacity: 0.7 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          );
        })}
        {comments.length === 0 && (
          <Typography variant="body2" color="text.disabled" align="center" sx={{ py: 2 }}>
            <FormattedMessage id="projectStepManaging.noCommentYet" defaultMessage="Henüz yorum eklenmemiş" />
          </Typography>
        )}
      </Stack>
    </Popover>
  );
};

export default CommentsListPopover;
